import React, { useState, useMemo, useCallback } from 'react';
import PlanningPanelCollapsed from './PlanningPanelCollapsed';
import PlanningPanelExpanded from './PlanningPanelExpanded';
import AffectationModal from '@/components/calendar/AffectationModal';
import { useCalendar } from '@/components/calendar/hooks/useCalendar';
import { useCalendarAffectations } from '@/components/calendar/hooks/useCalendarAffectations';
import { useDisponibilites } from '@/hooks/useDisponibilites';
import { usePeriodSync } from '@/hooks/usePeriodSync';
import { usePlanningStore } from '@/stores/planningStore';
import { usePilotageStore } from '@/stores/pilotageStore';
import type { CalendarAffectation } from '@/types/calendar';

interface PlanningPanelProps {
  entrepriseId: string;
  entrepriseNom?: string;
}

/**
 * Panneau Planning - Orchestration de l'état ouvert/fermé
 * Utilise Zustand pour permettre l'ouverture depuis d'autres pages
 */
const PlanningPanel = ({ entrepriseId, entrepriseNom }: PlanningPanelProps) => {
  // Store global pour état ouvert/fermé
  const { isOpen, openPanel, closePanel, activeTab, setActiveTab } = usePlanningStore();
  const closePilotage = usePilotageStore((state) => state.closePanel);

  // Handler pour ouvrir le panneau (ferme d'abord Pilotage)
  const handleOpenPanel = useCallback(() => {
    closePilotage();
    openPanel();
  }, [closePilotage, openPanel]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAffectation, setEditingAffectation] = useState<CalendarAffectation | null>(null);

  // Charger les données
  const {
    affectations,
    chantiers,
    membres,
    equipes,
    loading,
    createAffectation,
    updateAffectation,
    deleteAffectation,
  } = useCalendarAffectations({ entrepriseId });

  // Disponibilités des membres (pour section rotation)
  const { membresDisponibles, membresAffectes, refresh: refreshDisponibilites } = useDisponibilites({ entrepriseId });

  // Synchronisation automatique à 13h/17h/minuit (rafraîchit les disponibilités)
  usePeriodSync(refreshDisponibilites);

  // Logique calendrier
  const calendar = useCalendar({ affectations });

  // Compter les affectations d'aujourd'hui pour le badge
  const todayCount = useMemo(() => {
    const today = new Date();
    return affectations.filter((aff) => {
      const debut = new Date(aff.date_debut);
      const fin = new Date(aff.date_fin);
      return today >= debut && today <= fin;
    }).length;
  }, [affectations]);

  // Spécialités actives aujourd'hui (pour les points colorés)
  const todayActiveSpecialites = useMemo(() => {
    const today = new Date();
    const todayAffectations = affectations.filter((aff) => {
      const debut = new Date(aff.date_debut);
      const fin = new Date(aff.date_fin);
      return today >= debut && today <= fin;
    });
    return [
      ...new Set(
        todayAffectations.map(
          (a) => a.equipe?.specialite || a.membre?.specialite || 'default'
        )
      ),
    ];
  }, [affectations]);

  // Handlers
  const handleDayClick = (date: Date) => {
    calendar.selectDate(date);
  };

  const handleCreateAffectation = () => {
    setEditingAffectation(null);
    setIsModalOpen(true);
  };

  const handleEditAffectation = (affectation: CalendarAffectation) => {
    setEditingAffectation(affectation);
    setIsModalOpen(true);
  };

  const handleDeleteAffectation = async (id: string) => {
    if (confirm('Supprimer cette affectation ?')) {
      await deleteAffectation(id);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAffectation(null);
  };

  return (
    <>
      {/* ═══════════════════════════════════════════════════════════════
          ONGLET PLIÉ - Toujours visible quand fermé
          ═══════════════════════════════════════════════════════════════ */}
      <PlanningPanelCollapsed
        isVisible={!isOpen}
        onExpand={handleOpenPanel}
        todayCount={todayCount}
        activeSpecialites={todayActiveSpecialites}
      />

      {/* ═══════════════════════════════════════════════════════════════
          OVERLAY - Fond sombre quand ouvert
          ═══════════════════════════════════════════════════════════════ */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-[55] backdrop-blur-sm"
          onClick={closePanel}
        />
      )}

      {/* ═══════════════════════════════════════════════════════════════
          PANNEAU DÉPLIÉ - Visible quand ouvert
          ═══════════════════════════════════════════════════════════════ */}
      <PlanningPanelExpanded
        isVisible={isOpen}
        onCollapse={closePanel}
        calendarDays={calendar.calendarDays}
        weekDays={calendar.weekDays}
        currentMonthLabel={calendar.currentMonthLabel}
        currentYear={calendar.currentYear}
        selectedDate={calendar.selectedDate}
        currentDate={calendar.currentDate}
        selectedDayPlanning={calendar.selectedDayPlanning}
        loading={loading}
        // Vue mode
        viewMode={calendar.viewMode}
        viewLabel={calendar.viewLabel}
        onToggleViewMode={calendar.toggleViewMode}
        affectations={calendar.affectations}
        // Navigation (goToNext/goToPrev gèrent semaine et mois selon viewMode)
        onGoToToday={calendar.goToToday}
        onGoToPrevMonth={calendar.goToPrev}
        onGoToNextMonth={calendar.goToNext}
        onDayClick={handleDayClick}
        onEditAffectation={handleEditAffectation}
        onDeleteAffectation={handleDeleteAffectation}
        onCreateAffectation={handleCreateAffectation}
        // Props pour section rotation
        activeTab={activeTab}
        onTabChange={setActiveTab}
        membresDisponibles={membresDisponibles}
        membresAffectes={membresAffectes}
        chantiers={chantiers}
        equipes={equipes}
        membres={membres}
        entrepriseId={entrepriseId}
        entrepriseNom={entrepriseNom}
        onRotationComplete={refreshDisponibilites}
      />

      {/* ═══════════════════════════════════════════════════════════════
          MODAL D'AFFECTATION
          ═══════════════════════════════════════════════════════════════ */}
      <AffectationModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        affectation={editingAffectation}
        defaultDate={calendar.selectedDate}
        chantiers={chantiers}
        membres={membres}
        equipes={equipes}
        onSave={createAffectation}
        onUpdate={updateAffectation}
      />
    </>
  );
};

export default PlanningPanel;
